/**
 * Итоги прогона в разрезе отдельных символов.
 *
 * Режимы, которые учат клавишам, считают результат не по элементу
 * упражнения, а по символу: сочетание «апр» — это три разные клавиши, и
 * общий итог по нему ничего не сказал бы о том, какая из них не даётся.
 *
 * Пакет не знает, что стоит за символом: сопоставление символа учебной
 * единице — дело продуктовой функции.
 */

import type { KeystrokeEvent, TypingError } from '@type2x/shared-contracts';

import { median } from './metrics.js';

/** Итог прогона по одному символу. */
export interface CharacterOutcome {
  readonly character: string;
  readonly attempts: number;
  readonly errors: number;
  readonly corrections: number;
  readonly medianLatencyMs: number;
}

/** Что считать: по умолчанию все символы текста, кроме пробела. */
export interface CharacterOutcomeOptions {
  /** Только эти символы; остальные не относятся к режиму и пропускаются. */
  readonly only?: ReadonlySet<string>;
}

/** Наблюдения по одному символу внутри прогона. */
interface Accumulator {
  attempts: number;
  errors: number;
  corrections: number;
  latencies: number[];
}

function emptyAccumulator(): Accumulator {
  return { attempts: 0, errors: 0, corrections: 0, latencies: [] };
}

/**
 * Сопоставляет нажатия символам целевого текста.
 *
 * Позиция в тексте однозначно определяет ожидаемый символ, поэтому ошибка
 * относится к той клавише, которую требовалось нажать, а не к той, которую
 * нажали: учится пользователь первой.
 *
 * @param text целевой текст прогона;
 * @param events нажатия прогона;
 * @param errors ошибки прогона;
 * @param options ограничение набора символов.
 */
export function collectCharacterOutcomes(
  text: string,
  events: readonly KeystrokeEvent[],
  errors: readonly TypingError[],
  options: CharacterOutcomeOptions = {},
): CharacterOutcome[] {
  const byCharacter = new Map<string, Accumulator>();
  const errorPositions = new Set(errors.map((error) => error.position));

  for (const [position, character] of [...text].entries()) {
    if (character === ' ') continue;
    if (options.only !== undefined && !options.only.has(character)) continue;

    const accumulator = byCharacter.get(character) ?? emptyAccumulator();
    accumulator.attempts += 1;

    const event = events[position];
    if (event !== undefined) {
      if (event.interKeyIntervalMs > 0) accumulator.latencies.push(event.interKeyIntervalMs);
      if (event.corrected) accumulator.corrections += 1;
    }
    if (errorPositions.has(position)) accumulator.errors += 1;

    byCharacter.set(character, accumulator);
  }

  return [...byCharacter.entries()].map(([character, accumulator]) => ({
    character,
    attempts: accumulator.attempts,
    errors: accumulator.errors,
    corrections: accumulator.corrections,
    medianLatencyMs: median(accumulator.latencies),
  }));
}
